import { useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { Typography } from '../ui';
import {
  documentBaseHref,
  frameLoadReducer,
  frameNavigatedAway,
  initialFrameLoadState,
  prepareHtmlDocument,
} from '../../../lib/html-doc-frame';
import styles from './DocViewerModal.module.css';

// An agent-authored HTML report, rendered in a hard-locked frame: opaque origin, no cookies, no bridge,
// no top-level navigation. Scripts run (reports embed chart libraries) but can reach nothing of ours.
//
// THE SANDBOX TOKEN LIST IS SECURITY, NOT CONFIGURATION. `allow-same-origin` must NEVER be added next
// to `allow-scripts`: that pair is equivalent to having no sandbox at all — the frame becomes
// same-origin with us, so its script could read our storage/session AND rewrite its own sandbox
// attribute from the inside. `allow-top-navigation` is likewise absent, so the report cannot replace the
// app in the tab; links escape via the injected `<base target="_blank">` instead.
// There is deliberately NO postMessage bridge and no `sym` marker here — the dashboard hosts' frame
// contract is not reused, only the idea of sandboxing.
//
// IN-FRAME navigation is a separate problem, and the honest position is that web cannot solve it the way
// the natives do: they run every navigation through `DocumentWebPolicy.decide` and cancel all of them, but
// AN IFRAME CANNOT INTERCEPT ITS OWN NAVIGATION. Two mitigations stand in for that gate — the injected CSP
// (`form-action 'none'` blocks a form submit outright; see `FRAME_CSP`) and the load guard below, which
// notices the ones no directive can block (scripted `location` assignment, `<meta http-equiv="refresh">`,
// `<a target="_self">`) and tears the frame down instead of leaving a foreign page inside our chrome.
export function HtmlDocView({ text, fileName, docUrl }: { text: string; fileName: string; docUrl: string | null }) {
  const { t } = useTranslation();
  const doc = useMemo(() => prepareHtmlDocument(text, docUrl ? documentBaseHref(docUrl) : null), [text, docUrl]);
  const [loadState, dispatch] = useReducer(frameLoadReducer, doc, initialFrameLoadState);
  useEffect(() => dispatch({ type: 'document', doc }), [doc]);

  if (frameNavigatedAway(loadState, doc)) {
    return (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary" component="p">
          {t('docViewer.error')}
        </Typography>
      </div>
    );
  }

  return (
    <iframe
      title={fileName}
      className={styles.frame}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={doc}
      onLoad={() => dispatch({ type: 'load' })}
    />
  );
}
